import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import MergeIcon from '@mui/icons-material/CallMerge';

import BoxContainer from '../../style-components/BoxContainer.jsx';
import MergeLearnUnitsModal from '../../style-components/MergeLearnUnitsModal.jsx';
import { getAllQuestions, mergeDuplicateQuestions } from '../../models/questions';
import { getAllImages } from '../../models/images';
import { hashImageUrl, hammingNormalized, mapWithConcurrency } from '../../utils/imageHash';

// Distancia de imagen por defecto (0 = idénticas). Menor = más estricto.
const DEFAULT_IMAGE_THRESHOLD = 0.1;

// Cota para almacenar pares por imagen: así mover el slider re-agrupa sin
// recalcular la matriz O(n²).
const MAX_IMAGE_DISTANCE = 0.3;

// Similitud de contenido por defecto (Jaccard de trigramas; 1 = idéntico tras
// normalizar). Ajustable por el usuario con un slider.
const DEFAULT_TEXT_THRESHOLD = 0.9;

// Cota mínima de similitud de texto para almacenar un par (rango del slider),
// así mover el slider re-agrupa sin recalcular la matriz O(n²).
const MIN_TEXT_SIMILARITY = 0.5;

// Concurrencia al hashear imágenes.
const HASH_CONCURRENCY = 6;

// Cantidad de grupos por página (solo afecta el render, no el análisis).
const GROUPS_PER_PAGE = 8;

/**
 * Normaliza el texto para comparar contenido casi idéntico: minúsculas, sin
 * acentos, sin signos de puntuación ni caracteres especiales, y con espacios
 * colapsados. Así diferencias triviales (¿?, ., mayúsculas, tildes) no cuentan.
 */
function normalizeText(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // quitar acentos/diacríticos
    .replace(/[^a-z0-9\s]/g, ' ') // quitar puntuación y caracteres especiales
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Conjunto de trigramas (sub-cadenas de 3 caracteres) del texto normalizado.
 */
function trigramSet(normalized) {
  const set = new Set();
  if (normalized.length < 3) {
    if (normalized.length > 0) set.add(normalized);
    return set;
  }
  for (let i = 0; i <= normalized.length - 3; i++) {
    set.add(normalized.slice(i, i + 3));
  }
  return set;
}

/**
 * Índice de Jaccard (0-1) entre dos conjuntos. 1 = idénticos.
 */
function jaccard(a, b) {
  if (a.size === 0 || b.size === 0) return 0;
  const [small, large] = a.size < b.size ? [a, b] : [b, a];
  let intersection = 0;
  small.forEach(item => {
    if (large.has(item)) intersection += 1;
  });
  return intersection / (a.size + b.size - intersection);
}

/**
 * Agrupa por union-find. Dos preguntas se unen si su contenido es
 * suficientemente similar (>= textThreshold) y, ADEMÁS, cuando ambas tienen
 * imagen legible (pair.imageDist !== null), si esa imagen está a una distancia
 * <= imageThreshold. Si alguna del par no tiene imagen legible (imageDist ===
 * null), basta la similitud de texto. Devuelve grupos con más de un elemento.
 */
function groupByPairs(valid, pairs, textThreshold, imageThreshold) {
  const n = valid.length;
  const parent = Array.from({ length: n }, (_, i) => i);

  const find = x => {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    while (parent[x] !== root) {
      const next = parent[x];
      parent[x] = root;
      x = next;
    }
    return root;
  };
  const union = (a, b) => {
    parent[find(a)] = find(b);
  };

  pairs.forEach(pair => {
    const sameContent = pair.textSim >= textThreshold;
    if (!sameContent) return;
    if (pair.imageDist === null) {
      // Par sin imagen comparable (alguna no tiene imagen legible): solo texto.
      union(pair.a, pair.b);
      return;
    }
    // Par con ambas imágenes legibles: además la imagen debe ser casi idéntica.
    if (pair.imageDist <= imageThreshold) union(pair.a, pair.b);
  });

  const groupsByRoot = {};
  for (let i = 0; i < n; i++) {
    const root = find(i);
    if (!groupsByRoot[root]) groupsByRoot[root] = [];
    groupsByRoot[root].push(valid[i]);
  }

  return Object.values(groupsByRoot)
    .filter(group => group.length > 1)
    .sort((a, b) => b.length - a.length);
}

/** Nombre legible del propietario ({ firstnames, lastnames }). */
function formatOwner(question) {
  if (!question.owner) return 'Sin propietario';
  const name = `${question.owner.firstnames ?? ''} ${question.owner.lastnames ?? ''}`.trim();
  return name || 'Sin propietario';
}

/**
 * Unidad de aprendizaje. En producción aún se usa la relación antigua (objeto
 * singular `learnunit`); el respaldo cubre la nueva relación N:M (`learnUnits`).
 */
function formatLearnunit(question) {
  if (question.learnunit?.name) return question.learnunit.name;
  if (Array.isArray(question.learnUnits) && question.learnUnits.length > 0) {
    return question.learnUnits.map(unit => unit.name).join(', ');
  }
  return 'Sin unidad';
}

/**
 * Reúne, sin duplicados, todas las unidades de aprendizaje presentes en las
 * preguntas de un grupo. Contempla la relación N:M (`learnUnits`, array) y la
 * relación antigua singular (`learnunit`). Solo incluye unidades con id, porque
 * es lo que necesita el backend para reasignarlas a la pregunta conservada.
 */
function collectGroupLearnUnits(group) {
  const byId = new Map();
  group.forEach(question => {
    const units = [];
    if (Array.isArray(question.learnUnits)) units.push(...question.learnUnits);
    if (question.learnunit && question.learnunit.id != null) units.push(question.learnunit);
    units.forEach(unit => {
      if (unit && unit.id != null && !byId.has(unit.id)) {
        byId.set(unit.id, { id: unit.id, name: unit.name });
      }
    });
  });
  return [...byId.values()];
}

function RepeatedQuestionsList() {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [result, setResult] = useState(null); // { valid, pairs, total, withoutImage, unreadableImages }
  const [textThreshold, setTextThreshold] = useState(DEFAULT_TEXT_THRESHOLD);
  const [imageThreshold, setImageThreshold] = useState(DEFAULT_IMAGE_THRESHOLD);
  const [error, setError] = useState('');
  // Pregunta canónica elegida por grupo (clave = id de la primera del grupo).
  const [selectedCanonical, setSelectedCanonical] = useState({});
  const [mergingKey, setMergingKey] = useState(null);
  const [mergeError, setMergeError] = useState('');
  // Ids de preguntas ya fusionadas (se ocultan de los grupos).
  const [removedIds, setRemovedIds] = useState([]);
  const [page, setPage] = useState(1);
  // Grupo cuyo modal de fusión está abierto (con su pregunta a conservar y las
  // unidades de aprendizaje del grupo). null => modal cerrado.
  const [mergeTarget, setMergeTarget] = useState(null);

  // El agrupado es barato: re-corre union-find sobre los pares ya calculados.
  // Se filtran las preguntas ya fusionadas y los grupos que quedan con < 2.
  const groups = useMemo(() => {
    if (!result) return [];
    const base = groupByPairs(result.valid, result.pairs, textThreshold, imageThreshold);
    if (removedIds.length === 0) return base;
    const removed = new Set(removedIds);
    return base
      .map(group => group.filter(q => !removed.has(q.id)))
      .filter(group => group.length > 1);
  }, [result, textThreshold, imageThreshold, removedIds]);

  // Paginación solo del render de grupos (el análisis ya se hizo completo).
  const pageCount = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE));
  const pagedGroups = groups.slice((page - 1) * GROUPS_PER_PAGE, page * GROUPS_PER_PAGE);

  // Volver a la primera página cuando cambia el agrupado por los sliders.
  useEffect(() => {
    setPage(1);
  }, [textThreshold, imageThreshold]);

  // Acotar la página si el total de grupos baja (p. ej. al fusionar).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleScan = useCallback(async () => {
    setScanning(true);
    setScanned(false);
    setError('');
    setResult(null);
    setRemovedIds([]);
    setSelectedCanonical({});
    setMergeError('');
    setPage(1);

    try {
      const [questions, images] = await Promise.all([getAllQuestions(), getAllImages()]);

      if (!questions || questions.length === 0) {
        setResult({ valid: [], pairs: [], total: 0, withoutImage: 0, unreadableImages: 0 });
        setScanned(true);
        return;
      }

      // Mapa id -> imagen (para url de miniatura y para hashear solo las usadas).
      const imageById = {};
      (images || []).forEach(image => {
        imageById[image.id] = image;
      });

      // Hashear solo las imágenes referenciadas por alguna pregunta.
      const usedImageIds = [
        ...new Set(questions.map(q => q.image_id).filter(id => id != null && imageById[id])),
      ];
      const imageHashById = {};
      await mapWithConcurrency(usedImageIds, HASH_CONCURRENCY, async id => {
        imageHashById[id] = await hashImageUrl(imageById[id].url);
      });

      // Preparar cada pregunta: huella textual + hash de imagen (si tiene una
      // legible). Se distingue entre no tener imagen y tenerla pero no poder
      // leerla, porque en ambos casos el par se comparará solo por texto.
      const prepared = questions.map(question => {
        const image = question.image_id != null ? imageById[question.image_id] : null;
        const hasImageRef = question.image_id != null && image != null;
        const imageHash = hasImageRef ? imageHashById[question.image_id] || null : null;
        return {
          question: { ...question, _imageUrl: image?.url || null },
          sig: trigramSet(normalizeText(question.content)),
          imageHash,
          hasImageRef,
        };
      });

      // Ahora se analizan TODAS las preguntas. La imagen es criterio adicional
      // solo cuando ambas preguntas del par tienen imagen legible; si alguna no
      // tiene imagen (o no se pudo leer), el par se compara solo por texto.
      const valid = prepared.map(item => item.question);

      // Preguntas sin imagen: se comparan solo por texto (comportamiento esperado).
      const withoutImage = prepared.filter(item => !item.hasImageRef).length;
      // Preguntas con imagen que no se pudo leer/hashear (p. ej. CORS o URL
      // caída): también caen al criterio solo-texto, lo que podría agruparlas
      // por contenido aunque su imagen sea distinta.
      const unreadableImages = prepared.filter(
        item => item.hasImageRef && !item.imageHash
      ).length;

      // Matriz O(n²): se guardan los pares que podrían agruparse con alguna
      // combinación de sliders, para re-agrupar al moverlos sin recalcular.
      // - Ambas con imagen legible: se guarda imageDist (criterio texto + imagen).
      // - Alguna sin imagen legible: imageDist = null (criterio solo texto).
      const pairs = [];
      for (let i = 0; i < prepared.length; i++) {
        for (let j = i + 1; j < prepared.length; j++) {
          const textSim = jaccard(prepared[i].sig, prepared[j].sig);
          if (textSim < MIN_TEXT_SIMILARITY) continue;
          const bothHaveImage = prepared[i].imageHash && prepared[j].imageHash;
          if (bothHaveImage) {
            const imageDist = hammingNormalized(prepared[i].imageHash, prepared[j].imageHash);
            if (imageDist <= MAX_IMAGE_DISTANCE) {
              pairs.push({ a: i, b: j, textSim, imageDist });
            }
          } else {
            pairs.push({ a: i, b: j, textSim, imageDist: null });
          }
        }
      }

      setResult({ valid, pairs, total: prepared.length, withoutImage, unreadableImages });
      setScanned(true);
    } catch (err) {
      setError('Ocurrió un error al obtener o procesar las preguntas.');
    } finally {
      setScanning(false);
    }
  }, []);

  // Abrir el modal de fusión: calcula qué pregunta se conserva y las unidades de
  // aprendizaje presentes en el grupo (para elegir las de la pregunta conservada).
  const openMergeModal = useCallback((group, keepId) => {
    setMergeError('');
    setMergeTarget({
      group,
      keepId,
      learnUnits: collectGroupLearnUnits(group),
    });
  }, []);

  const closeMergeModal = useCallback(() => {
    setMergeTarget(null);
  }, []);

  // Confirmación desde el modal: fusiona el grupo y deja en la pregunta
  // conservada exactamente las unidades de aprendizaje seleccionadas (el backend
  // las fija dentro de la misma transacción).
  const handleConfirmMerge = useCallback(
    async selectedLearnUnitIds => {
      if (!mergeTarget) return;
      const { group, keepId } = mergeTarget;
      const deleteIds = group.map(q => q.id).filter(id => id !== keepId);
      if (deleteIds.length === 0) return;

      setMergingKey(group[0].id);
      setMergeError('');

      try {
        const response = await mergeDuplicateQuestions({
          keepId,
          deleteIds,
          learnunitIds: selectedLearnUnitIds,
        });
        if (response?.status === 200) {
          setRemovedIds(prev => [...prev, ...deleteIds]);
          setMergeTarget(null);
        } else {
          setMergeError(response?.response?.data || 'No se pudieron fusionar las preguntas.');
        }
      } catch (e) {
        setMergeError('No se pudieron fusionar las preguntas.');
      } finally {
        setMergingKey(null);
      }
    },
    [mergeTarget]
  );

  const totalQuestions = result?.total ?? 0;
  const analyzedQuestions = result ? result.valid.length : 0;
  const duplicatesCount = groups.reduce((acc, group) => acc + group.length, 0);

  return (
    <BoxContainer>
      <div className='w-full p-6 sm:p-8'>
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          Preguntas repetidas
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          Detecta preguntas duplicadas por contenido. Si ambas preguntas tienen imagen, además se
          exige que la imagen sea (casi) idéntica; si alguna no tiene imagen (o no se pudo leer),
          se comparan solo por texto. Ambas sensibilidades se ajustan abajo. El análisis se realiza
          por completo en el navegador.
        </Typography>

        <div className='flex flex-col sm:flex-row sm:items-start gap-6 mb-6'>
          <Button
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={handleScan}
            disabled={scanning}>
            {scanning ? 'Analizando…' : 'Buscar duplicadas'}
          </Button>

          <div className='flex-1 max-w-xs'>
            <Typography variant='caption' color='text.secondary'>
              Similitud de texto mínima: {textThreshold.toFixed(2)}
            </Typography>
            <Slider
              value={textThreshold}
              onChange={(_, value) => setTextThreshold(value)}
              min={MIN_TEXT_SIMILARITY}
              max={1}
              step={0.01}
              size='small'
              disabled={scanning}
              valueLabelDisplay='auto'
            />
            <Typography variant='caption' color='text.secondary'>
              Mayor = más estricto (contenido casi idéntico). Menor = agrupa más.
            </Typography>
          </div>

          <div className='flex-1 max-w-xs'>
            <Typography variant='caption' color='text.secondary'>
              Distancia de imagen máxima: {imageThreshold.toFixed(2)}
            </Typography>
            <Slider
              value={imageThreshold}
              onChange={(_, value) => setImageThreshold(value)}
              min={0}
              max={MAX_IMAGE_DISTANCE}
              step={0.01}
              size='small'
              disabled={scanning}
              valueLabelDisplay='auto'
            />
            <Typography variant='caption' color='text.secondary'>
              Menor = más estricto (solo casi idénticas). Mayor = agrupa más.
            </Typography>
          </div>
        </div>

        {scanning ? (
          <div className='flex items-center gap-2 mb-4'>
            <CircularProgress size={16} />
            <Typography variant='body2'>Procesando preguntas e imágenes…</Typography>
          </div>
        ) : null}

        {error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {scanned && !scanning ? (
          <>
            <Typography variant='body2' sx={{ mb: 2 }}>
              Se analizaron <strong>{analyzedQuestions}</strong> de{' '}
              <strong>{totalQuestions}</strong> preguntas. Se encontraron{' '}
              <strong>{groups.length}</strong> grupo(s) de repetidas ({duplicatesCount} preguntas).
            </Typography>

            {result?.withoutImage > 0 ? (
              <Alert severity='info' sx={{ mb: 2 }}>
                {result.withoutImage} pregunta(s) no tienen imagen: se compararon solo por su
                contenido de texto.
              </Alert>
            ) : null}

            {result?.unreadableImages > 0 ? (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {result.unreadableImages} pregunta(s) tienen imagen pero no se pudo leer (posible
                CORS o URL caída): se compararon solo por texto, por lo que podrían agruparse por
                contenido aunque su imagen sea distinta.
              </Alert>
            ) : null}

            {mergeError ? (
              <Alert severity='error' sx={{ mb: 2 }} onClose={() => setMergeError('')}>
                {mergeError}
              </Alert>
            ) : null}

            {groups.length === 0 ? (
              <Alert severity='success'>
                No se encontraron preguntas repetidas con la configuración actual.
              </Alert>
            ) : (
              <div className='flex flex-col gap-6'>
                {pagedGroups.map((group, index) => {
                  const groupKey = group[0].id;
                  const keepId = selectedCanonical[groupKey] ?? group[0].id;
                  const isMerging = mergingKey === groupKey;
                  const groupNumber = (page - 1) * GROUPS_PER_PAGE + index + 1;
                  return (
                    <div key={groupKey} className='border border-primary rounded-lg p-4'>
                      <div className='flex items-center justify-between gap-2 mb-1'>
                        <Typography variant='subtitle1' fontWeight='bold'>
                          Grupo {groupNumber} — {group.length} preguntas
                        </Typography>
                        <Button
                          variant='contained'
                          size='small'
                          startIcon={<MergeIcon />}
                          disabled={isMerging}
                          onClick={() => openMergeModal(group, keepId)}>
                          {isMerging ? 'Fusionando…' : 'Fusionar'}
                        </Button>
                      </div>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mb: 2, display: 'block' }}>
                        Marca la pregunta que se conservará; las demás se eliminarán y sus
                        referencias se reasignarán a ella.
                      </Typography>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        {group.map(question => {
                          const isKept = keepId === question.id;
                          return (
                            <div
                              key={question.id}
                              className={`flex gap-3 border rounded-md p-3 ${
                                isKept ? 'border-primary bg-primary/5' : 'border-gray-200'
                              }`}>
                              {question._imageUrl ? (
                                <a
                                  href={question._imageUrl}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='shrink-0'>
                                  <img
                                    src={question._imageUrl}
                                    alt={`Imagen de la pregunta ${question.id}`}
                                    className='w-20 h-20 object-contain bg-gray-50 rounded'
                                  />
                                </a>
                              ) : null}
                              <div className='flex flex-col gap-1 min-w-0'>
                                <label className='flex items-center gap-1 cursor-pointer'>
                                  <Radio
                                    size='small'
                                    checked={isKept}
                                    onChange={() =>
                                      setSelectedCanonical(prev => ({
                                        ...prev,
                                        [groupKey]: question.id,
                                      }))
                                    }
                                  />
                                  <Typography
                                    variant='caption'
                                    color={isKept ? 'primary' : 'text.secondary'}>
                                    {isKept ? 'Se conservará' : 'Conservar'}
                                  </Typography>
                                </label>
                                <Typography variant='body2'>{question.content}</Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  ID #{question.id}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  className='truncate'
                                  title={formatOwner(question)}>
                                  {formatOwner(question)}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  className='truncate'
                                  title={formatLearnunit(question)}>
                                  {formatLearnunit(question)}
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {pageCount > 1 ? (
                  <div className='flex justify-center mt-2'>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color='primary'
                    />
                  </div>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </div>

      <MergeLearnUnitsModal
        open={Boolean(mergeTarget)}
        onClose={closeMergeModal}
        learnUnits={mergeTarget?.learnUnits ?? []}
        keepId={mergeTarget?.keepId}
        deleteCount={
          mergeTarget
            ? mergeTarget.group.filter(q => q.id !== mergeTarget.keepId).length
            : 0
        }
        merging={Boolean(mergeTarget) && mergingKey === mergeTarget.group[0].id}
        onConfirm={handleConfirmMerge}
        entitySingular='pregunta'
        entityPlural='preguntas'
        deletedLabel='pregunta(s) duplicada(s)'
        referencesLabel='evaluaciones, intentos, mesas, unidades'
      />
    </BoxContainer>
  );
}

export default RepeatedQuestionsList;
